import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Grid,
  Typography,
  CardMedia,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Paper,
  Box,
} from '@mui/material';
import axios from 'axios';
import PropTypes from 'prop-types';

import ChurchSearchBar from './ChurchSearchBar';
import SERVER_API_URL from '../../../config';
import ChurchCard from '../cards/ChurchCard';

const MemoizedChurchCard = React.memo(({ church, handleDialog }) => {

  const twoLineNoWrapStyle = useMemo(
    () => ({
      overflow: 'hidden',
      display: '-webkit-box',
      WebkitBoxOrient: 'vertical',
      textOverflow: 'ellipsis',
      WebkitLineClamp: 2,
    }),
    []
  );

  return (
    <Grid item key={church.id} xs={12} sm={6} md={4}>
      <ChurchCard church={church} handleDialog={handleDialog} twoLineNoWrapStyle={twoLineNoWrapStyle} />
    </Grid>
  );
});

const ChurchList = ({ churchList }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [churches, setChurches] = useState(churchList);
  const [query, setQuery] = useState('');
  const [types, setTypes] = useState([]);
  const [oneChurch, setOneChurch] = useState();
  const [avatars, setAvatars] = useState({});

  const getAvatarById = useCallback(async (id) => {
    try {
      const response = await axios.get(`${SERVER_API_URL}/getChurchAvatar/getAvatarById/${id}`, {
        responseType: 'blob',
      });
      if (response.status === 200) {
        return URL.createObjectURL(response.data);
      }

      throw new Error('Failed to fetch avatar');
    } catch (e) {
      console.error('Error fetching avatar:', e);
      throw e;
    }
  }, []);

  useEffect(() => {
    const getChurchAvatars = (filteredChurches) => {
      if (Array.isArray(filteredChurches)) {
        filteredChurches.forEach((church) => {
          if (!avatars[church.id]) {
            getAvatarById(church.id)
              .then((url) => {
                setAvatars((prevUrls) => ({
                  ...prevUrls,
                  [church.id]: url,
                }));
              })
              .catch((error) => {
                console.error('Error fetching avatar:', error);
              });
          }
        });
      }
    };

    getChurchAvatars(churches);
  }, [churches, avatars, getAvatarById]);

  useEffect(() => {
    const filteredChurchesByQuery = churchList.filter((church) =>
      church.name.toLowerCase().includes(query.toLowerCase())
    );
    const filteredChurchesByType =
      types.length > 0
        ? filteredChurchesByQuery.filter((church) => types.includes(church.type))
        : filteredChurchesByQuery;
    setChurches(filteredChurchesByType);
  }, [query, types, churchList]);

  const handleDialog = (churchId) => {
    const filteredChurch = churchList.find((church) => church.id === churchId);
    setOneChurch(filteredChurch);
    setOpenDialog(true);
  };

  return (
    <>
      <Typography variant="h4" sx={{ p: 2, justifyContent: 'center', justifySelf: 'center' }}>
        የአድባራት ዝርዝር
      </Typography>
      <ChurchSearchBar setQuery={setQuery} setType={setTypes} />
      <Grid container spacing={4}>
        {churches &&
          churches.map((church) => (
            <MemoizedChurchCard key={church.id} church={church} handleDialog={handleDialog} />
          ))}
        {(!churches || churches.length === 0) && <Box sx={{ height: 200, p: 5, color: 'gray' }}>ውሂብ የለም።</Box>}
      </Grid>
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <Paper sx={{ minWidth: 500, minHeight: 500 }}>
          {oneChurch && avatars && avatars[oneChurch.id] && (
            <CardMedia
              component="div"
              sx={{
                // 16:9
                pt: '56.25%',
              }}
              image={oneChurch && avatars && avatars[oneChurch.id]}
            />
          )}
          {oneChurch && avatars && !avatars[oneChurch.id] && (
            <CardMedia
              component="div"
              sx={{
                // 16:9
                pt: '56.25%',
              }}
              image="/assets/landing/empty_church.png"
            />
          )}
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography gutterBottom variant="h5" component="h2">
                {oneChurch && oneChurch.name}
              </Typography>
              <Typography sx={{ minWidth: 100 }} gutterBottom variant="body1" color="darkgray">
                {oneChurch && oneChurch.type}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <DialogContentText> {oneChurch && oneChurch.description} </DialogContentText>
            <Divider sx={{ m: 2, fontSize: 24 }} />
            <DialogContentText> ታሪክ፡ {oneChurch && oneChurch.history}</DialogContentText>
            <DialogActions>
              <Button onClick={() => setOpenDialog(false)}>ተመለስ</Button>
            </DialogActions>
          </DialogContent>
        </Paper>
      </Dialog>
    </>
  );
};

export default ChurchList;

ChurchList.propTypes = {
  churchList: PropTypes.array.isRequired,
};

MemoizedChurchCard.propTypes = {
  church: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
  }).isRequired,
  handleDialog: PropTypes.func.isRequired,
  // avatars: PropTypes.object.isRequired,
};
